import { useField } from "formik";

import { TiVoTargetsTable, Button } from "components/elements";
import { ValidationBadge } from "components/form";
import { TiVoTargets } from "hooks/tivoApps";
import { useToggle } from "hooks/utils";
import { FieldProps } from "types/form";
import { getFieldTestId } from "utils/formik";

import { TEXTS } from "./constants";
import { TiVoDevicesModal } from "./TiVoDevicesModal";
import styles from "./TiVoTargetsField.scss";
import { useTiVoDeviceData } from "./useTiVoDeviceData";
import { adaptReturnValue } from "./utils";

interface TiVoTargetsFieldProps extends FieldProps {
  /** Target to which selection will be restricted. User will be only shown elements from provided target */
  restrictTargets?: TiVoTargets;
  /** Enables selecting all targets with same slug across groups */
  enableSlugBulkSelection?: boolean;
}

export function TiVoTargetsField({
  name,
  fields = [],
  restrictTargets,
  enableSlugBulkSelection,
  dataTestId,
}: TiVoTargetsFieldProps) {
  const [, { error, touched, value: targets }, { setValue }] =
    useField<TiVoTargets>(name);
  const tivoDeviceData = useTiVoDeviceData();
  const [open, toggleOpen] = useToggle(false);
  const testId = dataTestId || getFieldTestId(name);

  return (
    <div data-test-id={testId}>
      <div className={styles.targets}>
        <TiVoTargetsTable targets={targets} restrictTargets={restrictTargets} />
        <Button
          className={styles.addButton}
          dataTestId={`${testId}-edit-button`}
          onClick={toggleOpen}
        >
          {Object.keys(targets).length > 0
            ? TEXTS.EDIT_TARGET
            : TEXTS.ADD_TARGET}
        </Button>
        <TiVoDevicesModal
          fields={fields}
          isOpen={open}
          targets={targets}
          restrictTargets={restrictTargets}
          enableSlugBulkSelection={enableSlugBulkSelection}
          dataTestId={`${testId}-modal`}
          onClose={toggleOpen}
          onSave={(updatedTarget) => {
            setValue(adaptReturnValue(updatedTarget, tivoDeviceData));
            toggleOpen();
          }}
        />
      </div>
      <ValidationBadge error={error} touched={touched} />
    </div>
  );
}
